import { Button, Icon } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import qs from 'query-string';
import React, { useMemo, useEffect, useRef } from 'react';

import { getStoredStepUpState } from '../../api/fetch/stepup';
import { useAroIdMeResultContent } from '../../common/api';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';

import {
  StyledHeading,
  StyledBody,
  StyledIconWrapper,
  StyledIconBadge,
  StyledButtonWrapper,
  StyledOuterContainer,
  StyledFailureCard,
} from './styles';

export function AroIdMeVerificationSuccess() {
  const successContent = useAroIdMeResultContent('success');
  const trackEvent = useEvent();
  const headingRef = useRef<HTMLHeadingElement>(null);
  usePageLoadEvent('identity:mfa', 'id me verification success');

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleCTA = async () => {
    await trackEvent('identity – unified MFA CTAs', 'Back to sign in', 'unifiedmfactas', {}, true);
    navigate('/');
  };

  return (
    <PageLayout title={successContent.title} testId='route-/idme-callback/'>
      <Container height='auto' width='460px' data-testid='aro-verification-success'>
        <Logo />
        <StyledIconWrapper>
          <StyledIconBadge $variant='success'>
            <Icon name={successContent.icon} size='large' variant='outlined' color='#007636' width={40} height={40} />
          </StyledIconBadge>
        </StyledIconWrapper>
        <StyledHeading ref={headingRef} tabIndex={-1} variant='heading-medium-3' as='h1'>
          {successContent.title}
        </StyledHeading>
        <StyledBody variant='body-regular-m'>{successContent.body}</StyledBody>
        <StyledButtonWrapper>
          <Button
            variant='primary'
            size='large'
            onClick={handleCTA}
            testId='confirmation-cta'
            type='submit'
            label={successContent.ctaLabel}
            shape='square'
            fullWidth
          />
        </StyledButtonWrapper>
      </Container>
    </PageLayout>
  );
}

export function AroIdMeVerificationFailure() {
  const { getContentByPath } = useContent();
  const errorContent = useAroIdMeResultContent('error');
  const trackEvent = useEvent();
  const headingRef = useRef<HTMLHeadingElement>(null);
  usePageLoadEvent('identity:mfa', 'id me verification failure');
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  useEffect(() => {
    trackEvent('identity – unified MFA error', 'id me verification error', 'unifiedmfaerror', {
      errorField: 'ID.me Verification',
      errorDisplayed: getContentByPath('aroIdMeVerification.analyticsErrorDescription'),
    });
  }, [trackEvent, getContentByPath]);

  const handleCTA = async () => {
    await trackEvent('identity – unified MFA CTAs', 'Back to sign in', 'unifiedmfactas', {}, true);
    navigate('/');
  };

  return (
    <PageLayout title={errorContent.title} testId='route-/idme-callback/'>
      <StyledOuterContainer>
        <StyledFailureCard height='auto' width='460px' data-testid='aro-verification-failure'>
          <Logo />
          <StyledIconWrapper>
            <StyledIconBadge $variant='error'>
              <Icon name={errorContent.icon} size='large' variant='outlined' color='#D32F2F' width={40} height={40} />
            </StyledIconBadge>
          </StyledIconWrapper>
          <StyledHeading ref={headingRef} tabIndex={-1} variant='heading-medium-3' as='h1'>
            {errorContent.title}
          </StyledHeading>
          <StyledBody variant='body-regular-m'>{errorContent.body}</StyledBody>
          <StyledButtonWrapper>
            <Button
              variant='primary'
              size='large'
              onClick={handleCTA}
              testId='confirmation-cta'
              type='submit'
              label={errorContent.ctaLabel}
              shape='square'
              fullWidth
            />
          </StyledButtonWrapper>
        </StyledFailureCard>
      </StyledOuterContainer>
    </PageLayout>
  );
}

export function AroIdMeVerificationPage() {
  const { state, verificationKey } = qs.parse(window.location.search);
  const storedState = useMemo(() => {
    try {
      return getStoredStepUpState() || '';
    } catch {
      return '';
    }
  }, []);
  useEffect(() => {
    sessionStorage.removeItem('aro_remediation_csrf_state');
  }, []);

  const isStateValid = Boolean(state && storedState && state === storedState);

  if (!isStateValid) {
    return <AroIdMeVerificationFailure />;
  }

  return verificationKey ? <AroIdMeVerificationSuccess /> : <AroIdMeVerificationFailure />;
}
